import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { GripVertical, Check } from 'lucide-react';
import colors from '../../config/colors';

const DragRankQuestion = ({ question, value, onChange }) => {
  const maxSelect = question.maxSelect || 5;
  const [availableOptions] = useState(question.options);
  
  // Selected items are in the value array (ordered)
  // Unselected items are those not in value
  const selectedItems = value.map(val => 
    availableOptions.find(opt => opt.value === val)
  ).filter(Boolean);
  
  const unselectedItems = availableOptions.filter(opt => 
    !value.includes(opt.value)
  );

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    // Moving within selected list
    if (source.droppableId === 'selected' && destination.droppableId === 'selected') {
      const items = Array.from(selectedItems);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);
      onChange(items.map(item => item.value));
    }

    // Moving from unselected to selected
    if (source.droppableId === 'unselected' && destination.droppableId === 'selected') {
      if (selectedItems.length >= maxSelect) return; // Max reached
      
      const item = unselectedItems[source.index];
      const newSelected = [...selectedItems];
      newSelected.splice(destination.index, 0, item);
      onChange(newSelected.map(item => item.value));
    }

    // Moving from selected to unselected
    if (source.droppableId === 'selected' && destination.droppableId === 'unselected') {
      const newSelected = selectedItems.filter((_, index) => index !== source.index);
      onChange(newSelected.map(item => item.value));
    }
  };

  const toggleItem = (optionValue) => {
    if (value.includes(optionValue)) {
      // Remove from selected
      onChange(value.filter(v => v !== optionValue));
    } else if (selectedItems.length < maxSelect) {
      // Add to selected
      onChange([...value, optionValue]);
    }
  };

  return (
    <div style={{ padding: '12px 0' }}>
      <h2 style={{
        fontSize: 'clamp(1.5rem, 3vw, 2rem)',
        fontWeight: '700',
        color: colors.textPrimary,
        marginBottom: '8px',
        lineHeight: '1.2'
      }}>
        {question.question}
      </h2>

      {question.subtitle && (
        <p style={{
          fontSize: '1rem',
          color: colors.textSecondary,
          marginBottom: '20px'
        }}>
          {question.subtitle}
        </p>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        {/* Selected Items (Ranked) */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: '600',
            color: colors.brandPrimary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '16px'
          }}>
            Your Top {maxSelect} Priorities ({selectedItems.length}/{maxSelect} selected)
          </div>

          <Droppable droppableId="selected">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  background: snapshot.isDraggingOver ? colors.bgAccent : colors.bgCard,
                  border: `2px dashed ${snapshot.isDraggingOver ? colors.brandPrimary : colors.borderMedium}`,
                  borderRadius: '12px',
                  padding: '16px',
                  minHeight: '200px',
                  transition: 'all 0.3s ease'
                }}
              >
                {selectedItems.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: colors.textMuted,
                    fontSize: '0.95rem'
                  }}>
                    Click on options below to select, then drag to reorder
                  </div>
                ) : (
                  selectedItems.map((item, index) => (
                    <Draggable key={item.value} draggableId={item.value} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            marginBottom: '8px'
                          }}
                        >
                          <div style={{
                            background: snapshot.isDragging ? colors.brandAccent : colors.bgPage,
                            border: `2px solid ${colors.brandPrimary}`,
                            borderRadius: '8px',
                            padding: '14px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'move',
                            boxShadow: snapshot.isDragging ? '0 8px 24px rgba(0,0,0,0.15)' : 'none',
                            transition: 'all 0.2s ease'
                          }}>
                            <div {...provided.dragHandleProps} style={{
                              color: colors.brandPrimary,
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <GripVertical size={20} />
                            </div>
                            
                            <div style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: colors.brandPrimary,
                              color: colors.textInverse,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '700',
                              fontSize: '0.9rem',
                              flexShrink: 0
                            }}>
                              {index + 1}
                            </div>

                            <div style={{
                              flex: 1,
                              fontSize: '1rem',
                              fontWeight: '600',
                              color: colors.textPrimary
                            }}>
                              {item.label}
                            </div>

                            <button
                              onClick={() => toggleItem(item.value)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: colors.textMuted,
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                padding: '4px 8px',
                                transition: 'color 0.2s ease'
                              }}
                              onMouseOver={(e) => e.target.style.color = colors.brandPrimary}
                              onMouseOut={(e) => e.target.style.color = colors.textMuted}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

        {/* Unselected Items */}
        {unselectedItems.length > 0 && (
          <div>
            <div style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '16px'
            }}>
              Other Options (Click to add)
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '12px'
            }}>
              {unselectedItems.map((item) => (
                <button
                  key={item.value}
                  onClick={() => toggleItem(item.value)}
                  disabled={selectedItems.length >= maxSelect}
                  style={{
                    background: colors.bgCard,
                    border: `2px solid ${colors.borderMedium}`,
                    borderRadius: '8px',
                    padding: '14px 16px',
                    textAlign: 'center',
                    cursor: selectedItems.length >= maxSelect ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    color: selectedItems.length >= maxSelect ? colors.textMuted : colors.textPrimary,
                    opacity: selectedItems.length >= maxSelect ? 0.5 : 1
                  }}
                  onMouseOver={(e) => {
                    if (selectedItems.length < maxSelect) {
                      e.target.style.borderColor = colors.brandPrimary;
                      e.target.style.background = colors.bgAccent;
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = colors.borderMedium;
                    e.target.style.background = colors.bgCard;
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </DragDropContext>
    </div>
  );
};

export default DragRankQuestion;
